import React from "react";
import { Button } from "@/components/ui/button";
import { GraduationCap, Trash2, Search, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import Toast from "@/pages/portal/components/Toast";

const months = [
    { label: "January", value: "01" },
    { label: "February", value: "02" },
    { label: "March", value: "03" },
    { label: "April", value: "04" },
    { label: "May", value: "05" },
    { label: "June", value: "06" },
    { label: "July", value: "07" },
    { label: "August", value: "08" },
    { label: "September", value: "09" },
    { label: "October", value: "10" },
    { label: "November", value: "11" },
    { label: "December", value: "12" },
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: (currentYear + 2) - (currentYear - 50) + 1 }, (_, i) => {
    const year = (currentYear + 2 - i).toString();
    return { label: year, value: year };
});

// Letter grades for dropdown
const letterGrades = [
    { label: "A+", value: "A+" },
    { label: "A", value: "A" },
    { label: "A-", value: "A-" },
    { label: "B+", value: "B+" },
    { label: "B", value: "B" },
    { label: "B-", value: "B-" },
    { label: "C+", value: "C+" },
    { label: "C", value: "C" },
    { label: "C-", value: "C-" },
    { label: "D+", value: "D+" },
    { label: "D", value: "D" },
    { label: "D-", value: "D-" },
    { label: "F", value: "F" },
];

// Helper to get max GPA for numeric grading systems
const getMaxGpa = (gpaType: string): number | null => {
    const normalized = gpaType.toUpperCase();
    if (normalized.includes("OUT OF 4")) return 4;
    if (normalized.includes("OUT OF 6")) return 6;
    if (normalized.includes("OUT OF 20")) return 20;
    if (normalized.includes("OUT OF 100")) return 100;
    if (normalized.includes("OUT OF 110")) return 110;
    return null;
};

// Helper to determine input type for grading system
const getGpaInputType = (gpaType: string): "number" | "letter" | "text" => {
    const normalized = gpaType.toUpperCase();
    if (normalized.includes("OUT OF")) return "number";
    if (normalized.includes("LETTER")) return "letter";
    return "text";
};

interface Country {
    label: string;
    value: string;
}

interface Degree {
    label: string;
    value: string;
}

interface GradingSystem {
    label: string;
    value: string;
}

interface EducationModalProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    editingItem: any;
    selectedCountry: string;
    setSelectedCountry: (country: string) => void;
    openCountryPicker: boolean;
    setOpenCountryPicker: (open: boolean) => void;
    selectedDegree: string;
    setSelectedDegree: (degree: string) => void;
    selectedGpaType: string;
    setSelectedGpaType: (gpaType: string) => void;
    onDelete: () => void;
    onSave: (data: any) => Promise<void> | void;
    gradingSystems?: GradingSystem[];
    degrees?: Degree[];
    countries?: Country[];
    hideGpa?: boolean;
}

export default function EducationModal({
    isOpen,
    onOpenChange,
    editingItem,
    selectedCountry,
    setSelectedCountry,
    openCountryPicker,
    setOpenCountryPicker,
    selectedDegree,
    setSelectedDegree,
    selectedGpaType,
    setSelectedGpaType,
    onDelete,
    onSave,
    gradingSystems = [],
    degrees = [],
    countries = [],
    hideGpa = false,
}: EducationModalProps) {
    const [name, setName] = React.useState(editingItem?.name || "");
    const [field, setField] = React.useState(editingItem?.field || "");
    const [gpa, setGpa] = React.useState(editingItem?.gpa || "");
    const [gradYear, setGradYear] = React.useState(editingItem?.year || "");
    const [beginMonth, setBeginMonth] = React.useState(editingItem?.beginMonth || "");
    const [beginYear, setBeginYear] = React.useState(editingItem?.beginYear || "");
    const [endMonth, setEndMonth] = React.useState(editingItem?.endMonth || "");
    const [endYear, setEndYear] = React.useState(editingItem?.endYear || "");
    const [errors, setErrors] = React.useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = React.useState(false);
    const [saveError, setSaveError] = React.useState<string | null>(null);
    const [showValidationToast, setShowValidationToast] = React.useState(false);
    const scrollableRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (isOpen) {
            setName(editingItem?.name || "");
            setField(editingItem?.field || "");
            setGpa(editingItem?.gpa || "");
            setGradYear(editingItem?.year || "");
            setBeginMonth(editingItem?.beginMonth || "");
            setBeginYear(editingItem?.beginYear || "");
            setEndMonth(editingItem?.endMonth || "");
            setEndYear(editingItem?.endYear || "");
            setErrors({});
            setShowValidationToast(false);
        }
    }, [isOpen, editingItem]);

    const handleSave = async () => {
        const newErrors: Record<string, string> = {};
        if (!name.trim()) {
            newErrors.name = "Institution name is required.";
        }
        if (!selectedCountry) {
            newErrors.country = "Country is required.";
        }
        if (!selectedDegree) {
            newErrors.degree = "Degree is required.";
        }
        if (selectedDegree === "Secondary School") {
            if (!gradYear) {
                newErrors.gradYear = "Graduation year is required.";
            }
        } else if (selectedDegree) {
            if (!field.trim()) {
                newErrors.field = "Field of study is required.";
            }
            if (!beginMonth) {
                newErrors.beginMonth = "Begin month is required.";
            }
            if (!beginYear) {
                newErrors.beginYear = "Begin year is required.";
            }
            if (!endMonth) {
                newErrors.endMonth = "End month is required.";
            }
            if (!endYear) {
                newErrors.endYear = "Graduation year is required.";
            }
            // Validate end year is not before start year
            if (beginYear && endYear) {
                const startYear = parseInt(beginYear, 10);
                const endYearNum = parseInt(endYear, 10);
                if (endYearNum < startYear) {
                    newErrors.endYear = "Graduation year cannot be before begin year.";
                }
            }
        }
        // Only validate GPA if not hidden (applies to all degrees including Secondary School)
        if (!hideGpa && selectedDegree) {
            if (!selectedGpaType) {
                newErrors.gpaType = "GPA type is required.";
            }
            if (selectedGpaType && !gpa.trim()) {
                newErrors.gpa = "GPA is required.";
            }
            // Validate GPA based on grading system type
            if (selectedGpaType && gpa.trim()) {
                const inputType = getGpaInputType(selectedGpaType);
                if (inputType === "number") {
                    const maxGpa = getMaxGpa(selectedGpaType);
                    const numGpa = parseFloat(gpa);
                    if (isNaN(numGpa)) {
                        newErrors.gpa = "GPA must be a valid number.";
                    } else if (numGpa < 0) {
                        newErrors.gpa = "GPA cannot be negative.";
                    } else if (maxGpa !== null && numGpa > maxGpa) {
                        newErrors.gpa = `GPA cannot exceed ${maxGpa}.`;
                    }
                } else if (inputType === "letter") {
                    const validGrades = letterGrades.map(grade => grade.value);
                    if (!validGrades.includes(gpa)) {
                        newErrors.gpa = "Please select a valid letter grade.";
                    }
                } else if (inputType === "text") {
                    if (gpa.length > 255) {
                        newErrors.gpa = "GPA cannot exceed 255 characters.";
                    }
                }
            }
        }
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            setShowValidationToast(true);
            const fieldOrder = ["name", "degree", "country", "field", "gradYear", "beginMonth", "beginYear", "endMonth", "endYear", "gpaType", "gpa"];
            const firstErrorField = fieldOrder.find((field) => newErrors[field]);
            if (firstErrorField) {
                setTimeout(() => {
                    const target = document.getElementById(`edu-field-${firstErrorField}`) ?? scrollableRef.current?.querySelector(".border-red-400");
                    target?.scrollIntoView({ behavior: "smooth", block: "nearest" });
                }, 0);
            }
            return;
        }
        const countryLabel = countries.find((country) => country.value === selectedCountry)?.label || selectedCountry;
        setIsSaving(true);
        setSaveError(null);
        try {
            await onSave({
                id: editingItem?.id,
                name,
                degree: selectedDegree,
                country: countryLabel,
                field,
                gpa: hideGpa ? "" : gpa,
                gpaType: hideGpa ? "" : selectedGpaType,
                year: gradYear || endYear,
                beginMonth,
                beginYear,
                endMonth,
                endYear,
            });
            setErrors({});
            onOpenChange(false);
        } catch (error: any) {
            const msg = error?.response?.data?.detail || error?.message || "Failed to save. Please try again.";
            setSaveError(msg);
        } finally {
            setIsSaving(false);
        }
    };

    /* ------------------------------------------------------------------ */
    /* Field style helpers — centralised so every input looks identical   */
    /* ------------------------------------------------------------------ */
    const fieldCls = (error?: string) =>
        `h-11 rounded-lg border bg-white text-sm font-normal focus-visible:ring-2 focus-visible:ring-[#001B44]/15 focus-visible:border-[#001B44] transition-colors ${
            error ? "border-red-400 focus-visible:ring-red-200/60" : "border-slate-800"
        }`;
    const triggerCls = (error?: string) =>
        `h-11 rounded-lg border bg-white text-sm font-normal focus:ring-2 focus:ring-[#001B44]/15 focus:border-[#001B44] transition-colors ${
            error ? "border-red-400" : "border-slate-800"
        }`;

    /* Reusable section header */
    const SectionHeading = ({ children }: { children: React.ReactNode }) => (
        <div className="flex items-center gap-3 pb-1">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest whitespace-nowrap">
                {children}
            </span>
            <div className="flex-1 h-px bg-slate-100" />
        </div>
    );

    /* Reusable field label */
    const FieldLabel = ({ htmlFor, children, required }: { htmlFor?: string; children: React.ReactNode; required?: boolean }) => (
        <label htmlFor={htmlFor} className="block text-sm font-medium text-slate-700 mb-1.5">
            {children}
            {required && <span className="text-rose-400 text-xs ml-1">*</span>}
        </label>
    );

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[560px] max-h-[90vh] rounded-2xl p-0 border-none shadow-2xl flex flex-col bg-white overflow-hidden">
                {showValidationToast && (
                    <Toast
                        message="Please fill in all required fields."
                        type="error"
                        mode="absolute"
                        onClose={() => setShowValidationToast(false)}
                        duration={4000}
                    />
                )}

                {/* ── Header (sticky) ───────────────────────────────────── */}
                <DialogHeader className="px-6 pt-6 pb-5 shrink-0 border-b border-slate-100">
                    <div className="flex items-center gap-4">
                        <div className="w-11 h-11 rounded-xl bg-[#001B44]/[0.06] flex items-center justify-center text-[#001B44] shrink-0">
                            <GraduationCap className="w-5 h-5" />
                        </div>
                        <div>
                            <DialogTitle className="text-[17px] font-bold text-slate-900 leading-tight">
                                {editingItem ? "Edit Education" : "Add Education"}
                            </DialogTitle>
                            <p className="text-sm text-slate-400 mt-0.5">
                                {editingItem
                                    ? "Update your educational background."
                                    : "Add a degree or diploma to your profile."}
                            </p>
                        </div>
                    </div>
                </DialogHeader>

                {/* ── Scrollable body ───────────────────────────────────── */}
                <div ref={scrollableRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-7">

                    {/* Section 1 — Institution Information */}
                    <div className="space-y-4">
                        <SectionHeading>Institution Information</SectionHeading>

                        <div id="edu-field-name">
                            <FieldLabel required>Institution Name</FieldLabel>
                            <Input
                                value={name}
                                onChange={(event) => { setName(event.target.value); setErrors((prev) => ({ ...prev, name: "" })); }}
                                placeholder="e.g. Harvard University"
                                className={fieldCls(errors.name)}
                            />
                            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div id="edu-field-degree">
                                <FieldLabel required>Degree</FieldLabel>
                                <Select value={selectedDegree} onValueChange={(value) => { setSelectedDegree(value); setErrors((prev) => ({ ...prev, degree: "" })); }}>
                                    <SelectTrigger className={triggerCls(errors.degree)}>
                                        <SelectValue placeholder="Select degree" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[200px]">
                                        {degrees.map((degree) => (
                                            <SelectItem key={degree.value} value={degree.value}>{degree.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {errors.degree && <p className="text-xs text-red-500 mt-1">{errors.degree}</p>}
                            </div>

                            <div id="edu-field-country">
                                <FieldLabel required>Country</FieldLabel>
                                <Popover open={openCountryPicker} onOpenChange={setOpenCountryPicker}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className={`w-full h-11 justify-between rounded-lg border bg-white text-sm font-normal transition-colors focus:ring-2 focus:ring-[#001B44]/15 focus:border-[#001B44] ${
                                                errors.country ? "border-red-400" : "border-slate-800"
                                            } ${!selectedCountry ? "text-slate-400" : "text-slate-900"}`}
                                        >
                                            <span className="overflow-hidden whitespace-nowrap min-w-0">
                                                {selectedCountry
                                                    ? countries.find((c) => c.value === selectedCountry)?.label || selectedCountry
                                                    : "Select country…"}
                                            </span>
                                            <Search className="ml-2 h-4 w-4 shrink-0 text-slate-400" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        className="w-[--radix-popover-trigger-width] min-w-[200px] p-0 z-[150]"
                                        align="start"
                                        side="bottom"
                                        avoidCollisions={true}
                                        onWheel={(event) => event.stopPropagation()}
                                    >
                                        <Command>
                                            <CommandInput placeholder="Search country..." />
                                            <CommandList
                                                className="max-h-[200px] overflow-y-auto overscroll-contain"
                                                onWheel={(event) => {
                                                    const target = event.currentTarget;
                                                    target.scrollTop += event.deltaY;
                                                    event.preventDefault();
                                                }}
                                            >
                                                <CommandEmpty>No country found.</CommandEmpty>
                                                <CommandGroup>
                                                    {countries.map((country) => (
                                                        <CommandItem
                                                            key={country.value}
                                                            value={country.value}
                                                            onSelect={(value) => {
                                                                setSelectedCountry(value);
                                                                setOpenCountryPicker(false);
                                                                setErrors((prev) => ({ ...prev, country: "" }));
                                                            }}
                                                        >
                                                            <Check className={cn("mr-2 h-4 w-4", selectedCountry === country.value ? "opacity-100" : "opacity-0")} />
                                                            {country.label}
                                                        </CommandItem>
                                                    ))}
                                                </CommandGroup>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                                {errors.country && <p className="text-xs text-red-500 mt-1">{errors.country}</p>}
                            </div>
                        </div>

                        {selectedDegree !== "Secondary School" && selectedDegree && (
                            <div id="edu-field-field">
                                <FieldLabel required>Field of Study</FieldLabel>
                                <Input
                                    value={field}
                                    onChange={(event) => { setField(event.target.value); setErrors((prev) => ({ ...prev, field: "" })); }}
                                    placeholder="e.g. Computer Science"
                                    className={fieldCls(errors.field)}
                                />
                                {errors.field && <p className="text-xs text-red-500 mt-1">{errors.field}</p>}
                            </div>
                        )}
                    </div>

                    {/* Section 2 — Study Period (shown once a degree is selected) */}
                    {selectedDegree && (
                        <div className="space-y-4">
                            <SectionHeading>Study Period</SectionHeading>

                            {selectedDegree === "Secondary School" ? (
                                <div id="edu-field-gradYear">
                                    <FieldLabel required>Graduation Year</FieldLabel>
                                    <Select value={gradYear} onValueChange={(value) => { setGradYear(value); setErrors((prev) => ({ ...prev, gradYear: "" })); }}>
                                        <SelectTrigger className={triggerCls(errors.gradYear)}>
                                            <SelectValue placeholder="Select year" />
                                        </SelectTrigger>
                                        <SelectContent className="max-h-[200px]">
                                            {years.map((year) => (
                                                <SelectItem key={year.value} value={year.value}>{year.label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {errors.gradYear && <p className="text-xs text-red-500 mt-1">{errors.gradYear}</p>}
                                </div>
                            ) : (
                                <>
                                    {/* Begin date */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div id="edu-field-beginMonth">
                                            <FieldLabel required>Begin Month</FieldLabel>
                                            <Select value={beginMonth} onValueChange={(value) => { setBeginMonth(value); setErrors((prev) => ({ ...prev, beginMonth: "" })); }}>
                                                <SelectTrigger className={triggerCls(errors.beginMonth)}>
                                                    <SelectValue placeholder="Month" />
                                                </SelectTrigger>
                                                <SelectContent className="max-h-[200px]">
                                                    {months.map((month) => (
                                                        <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {errors.beginMonth && <p className="text-xs text-red-500 mt-1">{errors.beginMonth}</p>}
                                        </div>
                                        <div id="edu-field-beginYear">
                                            <FieldLabel required>Begin Year</FieldLabel>
                                            <Select value={beginYear} onValueChange={(value) => { setBeginYear(value); setErrors((prev) => ({ ...prev, beginYear: "" })); }}>
                                                <SelectTrigger className={triggerCls(errors.beginYear)}>
                                                    <SelectValue placeholder="Year" />
                                                </SelectTrigger>
                                                <SelectContent className="max-h-[200px]">
                                                    {years.map((year) => (
                                                        <SelectItem key={year.value} value={year.value}>{year.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {errors.beginYear && <p className="text-xs text-red-500 mt-1">{errors.beginYear}</p>}
                                        </div>
                                    </div>

                                    {/* End / Graduation date */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div id="edu-field-endMonth">
                                            <FieldLabel required>End Month</FieldLabel>
                                            <Select value={endMonth} onValueChange={(value) => { setEndMonth(value); setErrors((prev) => ({ ...prev, endMonth: "" })); }}>
                                                <SelectTrigger className={triggerCls(errors.endMonth)}>
                                                    <SelectValue placeholder="Month" />
                                                </SelectTrigger>
                                                <SelectContent className="max-h-[200px]">
                                                    {months.map((month) => (
                                                        <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {errors.endMonth && <p className="text-xs text-red-500 mt-1">{errors.endMonth}</p>}
                                        </div>
                                        <div id="edu-field-endYear">
                                            <FieldLabel required>Graduation Year</FieldLabel>
                                            <Select value={endYear} onValueChange={(value) => { setEndYear(value); setErrors((prev) => ({ ...prev, endYear: "" })); }}>
                                                <SelectTrigger className={triggerCls(errors.endYear)}>
                                                    <SelectValue placeholder="Year" />
                                                </SelectTrigger>
                                                <SelectContent className="max-h-[200px]">
                                                    {years.map((year) => (
                                                        <SelectItem key={year.value} value={year.value}>{year.label}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {errors.endYear && <p className="text-xs text-red-500 mt-1">{errors.endYear}</p>}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* Section 3 — Academic Performance */}
                    {!hideGpa && selectedDegree && (
                        <div className="space-y-4">
                            <SectionHeading>Academic Performance</SectionHeading>

                            <div>
                                <FieldLabel required>GPA Type</FieldLabel>
                                <Select
                                    value={selectedGpaType}
                                    onValueChange={(value) => { setSelectedGpaType(value); setGpa(""); setErrors((prev) => ({ ...prev, gpaType: "", gpa: "" })); }}
                                >
                                    <SelectTrigger className={triggerCls(errors.gpaType)}>
                                        <SelectValue placeholder="Select grading system" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-[200px]">
                                        {gradingSystems.map((system) => (
                                            <SelectItem key={system.value} value={system.value}>{system.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {errors.gpaType && <p className="text-xs text-red-500 mt-1">{errors.gpaType}</p>}
                            </div>

                            {selectedGpaType && (
                                <div>
                                    <FieldLabel required>GPA</FieldLabel>
                                    {getGpaInputType(selectedGpaType) === "number" ? (
                                        <>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max={getMaxGpa(selectedGpaType) ?? undefined}
                                                value={gpa}
                                                onChange={(event) => { setGpa(event.target.value); setErrors((prev) => ({ ...prev, gpa: "" })); }}
                                                placeholder={getMaxGpa(selectedGpaType) ? `0 – ${getMaxGpa(selectedGpaType)}` : "Enter GPA"}
                                                className={fieldCls(errors.gpa)}
                                            />
                                            {getMaxGpa(selectedGpaType) && (
                                                <p className="text-xs text-slate-400 mt-1">Enter a value between 0 and {getMaxGpa(selectedGpaType)}</p>
                                            )}
                                        </>
                                    ) : getGpaInputType(selectedGpaType) === "letter" ? (
                                        <Select value={gpa} onValueChange={(value) => { setGpa(value); setErrors((prev) => ({ ...prev, gpa: "" })); }}>
                                            <SelectTrigger className={triggerCls(errors.gpa)}>
                                                <SelectValue placeholder="Select letter grade" />
                                            </SelectTrigger>
                                            <SelectContent className="max-h-[200px]">
                                                {letterGrades.map((grade) => (
                                                    <SelectItem key={grade.value} value={grade.value}>{grade.label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <>
                                            <Input
                                                value={gpa}
                                                onChange={(event) => { setGpa(event.target.value); setErrors((prev) => ({ ...prev, gpa: "" })); }}
                                                placeholder="Enter GPA"
                                                maxLength={255}
                                                className={fieldCls(errors.gpa)}
                                            />
                                            <p className="text-xs text-slate-400 mt-1">{255 - gpa.length} characters remaining</p>
                                        </>
                                    )}
                                    {errors.gpa && <p className="text-xs text-red-500 mt-1">{errors.gpa}</p>}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Footer (sticky) ───────────────────────────────────── */}
                <DialogFooter className="px-6 pt-4 pb-5 bg-white border-t border-slate-100 shrink-0">
                    <div className="flex items-center justify-between w-full gap-3">
                        <div className="shrink-0">
                            {editingItem && (
                                <Button
                                    variant="ghost"
                                    onClick={onDelete}
                                    className="text-red-500 hover:text-red-600 hover:bg-red-50 font-medium px-3 h-10 text-sm"
                                >
                                    <Trash2 className="w-4 h-4 mr-1.5" />
                                    Delete
                                </Button>
                            )}
                        </div>
                        <div className="flex items-center gap-3">
                            {saveError && (
                                <p className="text-xs text-red-500">{saveError}</p>
                            )}
                            <Button
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                className="text-slate-500 hover:text-slate-700 font-medium h-10 text-sm px-4"
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="rounded-xl bg-[#001B44] hover:bg-[#002a6e] text-white font-semibold px-6 h-10 text-sm shadow-sm transition-colors"
                            >
                                {isSaving ? "Saving…" : "Save Education"}
                            </Button>
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
